import React, { ChangeEvent, FC, useContext, useEffect, useState } from 'react';
import Section from '../../../components/Main/Section/Section';
import { useTranslation } from 'react-i18next';
import { CurrentViewContext } from '../../../providers/CurrentViewProvider';
import {
  Box,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
  useTheme,
} from '@mui/material';
import ActionBar from '../../../components/ActionBar/ActionBar';
import { GMBAuthContext } from '../../../providers/GMBAuthProvider';
import Pagination from '../../../components/Pagination/Pagination';
import GoogleMyBusinessLogin from '../../../components/GoogleMyBusiness/GoogleMyBusinessLogin';
import LeftContainer from '../../../components/Containers/LeftContainer';
import SortContainer from '../../../components/Containers/SortContainer';
import ContentContainer from '../../../components/Containers/ContentContainer';
import { IGMBLocationsResponse } from '../../../shared/interfaces/IGMBLocationsResponse';
import GMBService from '../../../services/GMBService';
import LocationHistoryCard from '../../../components/LocationCard/LocationHistoryCard';
import { IGmbLocation } from '../../../shared/interfaces/IGmbLocation';
import { IGmbHistoryEntry } from '../../../shared/interfaces/IGmbHistoryEntry';
import { IGmbScoreEntry } from '../../../shared/interfaces/IGmbScoreEntry';

const LocationHistory: FC = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { setCurrentView } = useContext(CurrentViewContext);
  const { user, login } = useContext(GMBAuthContext);
  const [loading, setLoading] = useState(false);
  const [gmbResponse, setGmbResponse] = useState<IGmbHistoryEntry[]>([
    {
      scores: [
        {
          createdAt: '',
          id: '',
          isGenerated: false,
          location: '',
          score: {
            averageRating: 0,
            totalReviewCount: 0,
          },
          scrapedAt: '',
          updatedAt: '',
        },
      ],
      summary: {
        growthPercentage: 0,
        reviewCount: 0,
      },
    },
  ]);
  const [gmbScores, setGmbScores] = useState<IGmbScoreEntry[]>([
    {
      createdAt: '2021-10-13T09:00:00.000Z',
      growthMonthPercentage: 0,
      growthPercentage: 0,
      growthWeekPercentage: 0,
      id: '',
      isGenerated: false,
      location: '',
      reviewsWithComment: 0,
      score: {
        averageRating: 0,
        totalReviewCount: 0,
      },
      scrapedAt: '',
      updatedAt: '',
    },
  ]);

  //pagination start
  const [locationResponse, setLocationResponse] = useState<IGMBLocationsResponse>({
    locations: [],
    paging: {
      totalItems: 0,
      pagesTotal: 0,
    },
  });

  // TODO since our backend can now do chained sorting we can and probably should use multi-select component for orderBy
  const [orderBy, setOrderBy] = useState<string>('');
  const [offset, setOffset] = useState<number>(0);
  const [pageSize, setPageSize] = useState<string>('10');
  const [page, setPage] = useState<number>(1);
  const [mode, setMode] = useState<FilterMode>('YEAR');

  useEffect(() => {
    setCurrentView(t('location') + t('history').toLowerCase());
    document.title = `Repuation+ ${t('location') + t('history').toLowerCase()}`;
  }, [setCurrentView, t]);

  const handlePageNumberChange = (event: ChangeEvent<unknown>, pageNumber: number) => {
    setPage(pageNumber);
    setOffset(parseInt(pageSize) * pageNumber - parseInt(pageSize));
  };

  const handlePageSizeChange = (event: SelectChangeEvent) => {
    setOffset(0);
    setPage(1);
    setPageSize(event.target.value);
  };
  //pagination end

  // Location response

  useEffect(() => {
    if (!user) return;

    const controller = new AbortController();

    const getLocations = async () => {
      const data = await GMBService.getAllLocations(
        offset,
        parseInt(pageSize),
        setLoading,
        controller.signal,
        undefined,
        orderBy === '' ? undefined : [orderBy]
      );

      if (!data) return;
      if ('success' in data) return;

      setLocationResponse(data);
    };

    (async () => await getLocations())();

    return () => {
      controller.abort();
    };
  }, [user, offset, orderBy, pageSize]);

  // GMB Scores

  useEffect(() => {
    if (!user) return;

    const controller = new AbortController();

    const getScore = async () => {
      let scoresArray = [];
      for (const locationID of locationResponse.locations.map((location) => location.locationID)) {
        try {
          const response = await GMBService.getScores(
            locationID,
            mode,
            setLoading,
            controller.signal
          );

          if (response) {
            scoresArray.push(response);
          }
        } catch (error) {
          console.error('Error fetching scores for location:', locationID, error);
        }
      }
      setGmbResponse(scoresArray);
    };

    (async () => await getScore())();

    return () => {
      controller.abort();
    };
  }, [user, locationResponse.locations, mode]);

  useEffect(() => {
    if (!user) return;

    const controller = new AbortController();
    const scores: IGmbScoreEntry[] = [];

    const getLastScores = async () => {
      for (let id of locationResponse.locations) {
        const response = await GMBService.getLastScores(
          id.locationID,
          setLoading,
          controller.signal
        );
        scores.push(
          response || {
            createdAt: '2021-10-13T09:00:00.000Z',
            growthMonthPercentage: 0,
            growthPercentage: 0,
            growthWeekPercentage: 0,
            id: '',
            isGenerated: false,
            location: '',
            reviewsWithComment: 0,
            score: {
              averageRating: 0,
              totalReviewCount: 0,
            },
            scrapedAt: '',
            updatedAt: '',
          }
        );
      }
      setGmbScores(scores);
    };

    (async () => await getLastScores())();

    return () => {
      controller.abort();
    };
  }, [user, locationResponse.locations]);

  const renderLocationHistory = () => {
    if (locationResponse.locations.length === 0) {
      return (
        <Box>
          <Typography variant="body2">{'no_locations'}</Typography>
        </Box>
      );
    }

    return (
      <Box>
        {locationResponse.locations.map((location: IGmbLocation, index) => {
          const scoreEntry = gmbScores[index];
          if (!scoreEntry) {
            return null;
          }
          return (
            <Box key={index}>
              <LocationHistoryCard
                locationName={location.locationData.title}
                score={scoreEntry.score.averageRating}
                growth={scoreEntry.growthPercentage}
                reviews={scoreEntry.score.totalReviewCount}
                locationID={location.locationID}
                addressLines={location.locationData.address.addressLines}
                phoneNumber={location.locationData.phoneNumber}
                zip={location.locationData.address.postalCode}
                city={location.locationData.address.locality}
                latitude={location.locationData.location.latitude}
                longitude={location.locationData.location.longitude}
                times={location.locationData.times}
                status={location.locationData.status}
              />
            </Box>
          );
        })}
      </Box>
    );
  };

  return (
    <Section>
      {!user ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            flex: 1,
            borderRadius: `${theme.shape.borderRadius}px`,
            backgroundColor: theme.palette.background.paper,
          }}
        >
          <GoogleMyBusinessLogin login={login} />
        </Box>
      ) : (
        <ContentContainer>
          <LeftContainer>
            <ActionBar></ActionBar>
            <SortContainer>
              <Typography variant="body2">
                {`${locationResponse.paging.totalItems} ${t('locations')}`}
              </Typography>
              <FormControl
                size="small"
                sx={{ minWidth: '17.75rem' }}
              >
                <InputLabel id="sort-select-label">{t('sort')}</InputLabel>
                <Select
                  labelId="sort-select-label"
                  value={orderBy}
                  onChange={(event: SelectChangeEvent) => {
                    setOffset(0);
                    setPage(1);
                    setOrderBy(event.target.value);
                  }}
                  label={t('sort')}
                >
                  <MenuItem value="">
                    <em>{t('unsorted')}</em>
                  </MenuItem>
                  <MenuItem value="title asc">{t('location_name_asc')}</MenuItem>
                  <MenuItem value="title desc">{t('location_name_desc')}</MenuItem>
                </Select>
              </FormControl>
            </SortContainer>
            {loading ? (
              <Box>
                <LinearProgress sx={{ my: theme.spacing(1) }} />
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>{renderLocationHistory()}</Box>
            )}
            <Pagination
              count={locationResponse.paging.pagesTotal}
              page={page}
              itemDescription={t('locations')}
              showItemsPerPage={pageSize}
              onPageChange={handlePageNumberChange}
              onItemsPerPageChange={handlePageSizeChange}
            />
          </LeftContainer>
        </ContentContainer>
      )}
    </Section>
  );
};

export default LocationHistory;
